"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, Loader2 } from "lucide-react";

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { btnGhost as BTN_GHOST, btnPrimary as BTN_PRIMARY } from "@/components/editorial";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/analytics";

import {
  clearSession,
  newSessionId,
  saveSession,
} from "../lib/session-storage";
import {
  getSessionStatus,
  hasRecommendation,
  reentryTarget,
  type ZenyraStatus,
} from "../lib/session-status";
import { mockOpening } from "../lib/mock-ai";
import { SESSION_SCHEMA_VERSION, type SessionState } from "../lib/types";
import { relativeRo } from "../lib/time";
import { ACCESS_DENIED_MESSAGE, isAllowedName } from "../lib/access";
import { getTopicPrefill } from "../lib/topics";

const schema = z.object({
  concern: z
    .string()
    .trim()
    .min(4, "Scrie câteva cuvinte despre ce te frământă")
    .max(500, "E puțin cam lung — scurtează un pic"),
  name: z
    .string()
    .trim()
    .min(2, "Spune-mi cum să-ți zic (minim 2 litere)")
    .max(40, "Numele e prea lung")
    .refine((v) => isAllowedName(v), ACCESS_DENIED_MESSAGE),
  website: z.string().optional(),
});

type Values = z.infer<typeof schema>;

const QUICK_TOPICS = [
  { label: "Stres la muncă", value: "Mă simt stresat/ă la muncă și nu mai fac față." },
  { label: "Anxietate", value: "Mă lupt cu anxietatea de o vreme și aș vrea să vorbesc cu cineva." },
  { label: "Burnout", value: "Cred că am ajuns la burnout și nu mai știu cum să continui." },
  { label: "Relații", value: "Am dificultăți în relație și caut un terapeut potrivit." },
  { label: "Nu știu exact", value: "Nu știu exact ce simt, dar simt că am nevoie de ajutor." },
];

export default function IntakeForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const rawPrefill = (searchParams.get("prefill") ?? "").trim().slice(0, 500);
  const customPrefill = rawPrefill.length >= 4 ? rawPrefill : null;
  const topicPrefill = customPrefill ?? getTopicPrefill(searchParams.get("topic"));
  const [status, setStatus] = useState<ZenyraStatus | null>(null);
  const [confirmingNew, setConfirmingNew] = useState(false);

  useEffect(() => {
    setStatus(getSessionStatus());
  }, []);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { concern: topicPrefill ?? "", name: "", website: "" },
  });

  const session =
    status && (status.kind === "active" || status.kind === "ended")
      ? status.session
      : null;
  const reentry = reentryTarget(status);
  const sessionHasRec = session ? hasRecommendation(session) : false;

  const startNew = () => {
    clearSession();
    setStatus({ kind: "none" });
    setConfirmingNew(false);
  };
  const requestNew = () => {
    if (sessionHasRec) {
      setConfirmingNew(true);
      return;
    }
    startNew();
  };

  if (confirmingNew) {
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-[#264653]/12 bg-[#FFF9F5] p-4">
          <p className="text-[15px] font-semibold text-[#264653]">
            Pierzi recomandarea salvată
          </p>
          <p className="mt-1.5 text-[13.5px] leading-relaxed text-[#264653]/70">
            O conversație nouă înlocuiește recomandarea pe care o ai salvată pe
            acest dispozitiv. Sigur vrei să începi alta?
          </p>
        </div>
        <button type="button" onClick={startNew} className={`${BTN_PRIMARY} w-full`}>
          Da, începe alta
        </button>
        <button
          type="button"
          onClick={() => setConfirmingNew(false)}
          className={`${BTN_GHOST} w-full`}
        >
          Anulează
        </button>
      </div>
    );
  }

  if (session && reentry) {
    const lastUser = [...session.messages].reverse().find((m) => m.role === "user");
    const userMsgCount = session.messages.filter((m) => m.role === "user").length;
    const isEnded = status?.kind === "ended";
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-[#264653]/12 bg-[#FFF9F5] p-4">
          <p className="text-[15px] font-semibold text-[#264653]">
            {isEnded
              ? `Ai vorbit deja cu Zenyra, ${session.name}`
              : `Ai o conversație neterminată, ${session.name}`}
          </p>
          <p className="mt-1.5 text-[13.5px] text-[#264653]/70">
            {isEnded
              ? `Recomandarea din conversația ta de ${relativeRo(session.startedAt)} te așteaptă`
              : `${userMsgCount} mesaje · începută ${relativeRo(session.startedAt)}`}
          </p>
          {!isEnded && lastUser && (
            <div className="mt-3.5 border-t border-[#264653]/10 pt-3.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#264653]/50">
                Ultimul tău mesaj
              </p>
              <p className="mt-1.5 line-clamp-3 text-[14px] leading-relaxed text-[#264653]/80">
                {lastUser.content}
              </p>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => router.push(reentry.href)}
          className={`${BTN_PRIMARY} w-full`}
        >
          {isEnded ? "Vezi recomandarea ta" : "Reia conversația"}
          <ArrowRight className="size-4" />
        </button>
        <button type="button" onClick={requestNew} className={`${BTN_GHOST} w-full`}>
          Începe o sesiune nouă
        </button>
        <p className="text-center text-[12.5px] text-[#264653]/55">
          Conversația e salvată doar pe acest dispozitiv.
        </p>
      </div>
    );
  }

  async function onSubmit(values: Values) {
    if (values.website && values.website.trim() !== "") {
      // Bot — silently redirect away
      router.replace("/");
      return;
    }

    // reCAPTCHA is checked on the server when the chat makes its first
    // request (see ../lib/verification.ts), not here.

    const name = values.name.trim();
    const concern = values.concern.trim();
    const sessionId = newSessionId();
    const startedAt = Date.now();
    const opening = mockOpening(name);

    const messages: SessionState["messages"] = [
      {
        id: `m_${startedAt}`,
        role: "assistant",
        content: opening,
        createdAt: startedAt,
        kind: "contract",
      },
      {
        id: `m_${startedAt + 1}_u`,
        role: "user",
        content: concern,
        createdAt: startedAt + 1,
      },
    ];

    saveSession({
      v: SESSION_SCHEMA_VERSION,
      sessionId,
      name,
      startedAt,
      pendingFirstSend: true,
      firstUserMsgAt: startedAt + 1,
      messages,
    });

    const topic = searchParams.get("topic") ?? undefined;
    trackEvent("sesiune_ai_started", {
      source: "intake_form",
      has_name: true,
      topic,
      has_topic_prefill: !!topicPrefill,
    });
    router.push("/zenyra/chat");
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
        {/* Honeypot */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          {...form.register("website")}
          style={{
            position: "absolute",
            left: "-9999px",
            opacity: 0,
            height: 0,
            width: 0,
            pointerEvents: "none",
          }}
        />

        <FormField
          control={form.control}
          name="concern"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[14px] font-semibold text-[#264653]">
                Ce te frământă?
              </FormLabel>

              {/* Quick-topic chips — one tap to start */}
              {!field.value && (
                <div className="flex flex-wrap gap-2 pb-0.5">
                  {QUICK_TOPICS.map((topic) => (
                    <button
                      key={topic.label}
                      type="button"
                      onClick={() => {
                        form.setValue("concern", topic.value, { shouldValidate: true });
                        // Focus the name field since concern is now filled
                        const nameInput = document.querySelector<HTMLInputElement>('input[name="name"]');
                        nameInput?.focus();
                      }}
                      className="rounded-lg border border-[#264653]/15 px-3.5 py-1.5 text-[13.5px] font-medium text-[#264653]/75 transition-colors hover:border-[#264653]/40 hover:text-[#264653]"
                    >
                      {topic.label}
                    </button>
                  ))}
                </div>
              )}

              <FormControl>
                <Textarea
                  placeholder="Sau scrie-mi în câteva cuvinte ce te apasă acum…"
                  rows={3}
                  autoFocus={!topicPrefill}
                  className="rounded-lg border-[#264653]/15 bg-white text-base text-[#264653] placeholder:text-[#264653]/35 focus-visible:border-[#2C969B] focus-visible:ring-[3px] focus-visible:ring-[#2C969B]/20"
                  {...field}
                />
              </FormControl>
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-[#264653]/55">
                {field.value
                  ? "Poți modifica textul dacă vrei, sau lasă-l așa — e suficient."
                  : "Devine primul tău mesaj. Nu trebuie perfect — scrie cum îți vine."}
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[14px] font-semibold text-[#264653]">
                Cum să-ți zic?
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Orice nume sau poreclă"
                  autoComplete="given-name"
                  autoFocus={!!topicPrefill}
                  className="h-12 rounded-lg border-[#264653]/15 bg-white px-4 text-base text-[#264653] placeholder:text-[#264653]/35 focus-visible:border-[#2C969B] focus-visible:ring-[3px] focus-visible:ring-[#2C969B]/20"
                  {...field}
                />
              </FormControl>
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-[#264653]/55">
                Poate fi absolut orice — un prenume, o poreclă sau doar o inițială.
                Nu contează numele real, doar ca să știe Zenyra cum să ți se adreseze.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className={cn(BTN_PRIMARY, "w-full disabled:opacity-60")}
        >
          {form.formState.isSubmitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Se pregătește sesiunea…
            </>
          ) : (
            <>
              Începe sesiunea
              <ArrowRight className="size-4" />
            </>
          )}
        </button>

        <p className="text-center text-[12.5px] text-[#264653]/60">
          Continuând, accepți{" "}
          <Link
            href="/politica-gdpr"
            className="underline underline-offset-2 hover:text-[#264653]"
          >
            politica de confidențialitate
          </Link>
          .
        </p>
      </form>
    </Form>
  );
}
